import { MutableRefObject, useCallback, useRef, useState } from 'react';
import { isFunction } from 'lodash';

import { Point } from '../../../../../common/maths/geometry/pointTypes';
import { applyImageModalZoomTransform, ImageModalZoomTransformElements } from '../utils/imageModalZoomTransformUtils';
import { ImageModalZoomState, resetZoomState } from '../utils/imageModalZoomTypes';

export type CommitImageModalZoomState = (nextZoomState: ImageModalZoomState, options?: { syncReact?: boolean }) => void;

export type UpdateImageModalZoomState = (
    updater: ImageModalZoomState | ((currentZoomState: ImageModalZoomState) => ImageModalZoomState),
) => void;

type UseImageModalZoomStoreResult = {
    zoomState: ImageModalZoomState;
    zoomStateRef: MutableRefObject<ImageModalZoomState>;
    commitZoomState: CommitImageModalZoomState;
    updateZoomState: UpdateImageModalZoomState;
    registerZoomTransformElements: (elements: ImageModalZoomTransformElements) => void;
};

/**
 * Keeps the imperative zoom state and DOM transform in sync, with React state updated only when needed.
 */
export const useImageModalZoomStore = (): UseImageModalZoomStoreResult => {
    const [zoomState, setZoomState] = useState<ImageModalZoomState>(resetZoomState);
    const transformElementsRef = useRef<ImageModalZoomTransformElements>({
        panElement: null,
        frameElement: null,
    });
    // Authoritative for gesture/zoom maths; React zoomState is a display mirror updated via explicit commits.
    const zoomStateRef = useRef(zoomState);

    /**
     * Applies the current zoom transform directly to the registered DOM elements.
     */
    const applyZoomTransform = useCallback((scale: number, translation: Point) => {
        applyImageModalZoomTransform(transformElementsRef.current, { scale, translation });
    }, []);

    /**
     * Updates the authoritative zoom state and optionally syncs it into React state.
     */
    const commitZoomState = useCallback<CommitImageModalZoomState>(
        (nextZoomState, { syncReact = true } = {}) => {
            zoomStateRef.current = nextZoomState;
            applyZoomTransform(nextZoomState.scale, nextZoomState.translation);
            if (syncReact) setZoomState(nextZoomState);
        },
        [applyZoomTransform],
    );

    /**
     * Applies a zoom state update immediately against the authoritative imperative state.
     *
     * The update must not run inside a setZoomState updater: React executes updaters later
     * (and can replay them), so a deferred commit would rewind zoomStateRef and the DOM
     * transforms after imperative animation frames have already moved past it.
     */
    const updateZoomState = useCallback<UpdateImageModalZoomState>(
        (updater) => {
            const nextZoomState = isFunction(updater) ? updater(zoomStateRef.current) : updater;
            commitZoomState(nextZoomState);
        },
        [commitZoomState],
    );

    /**
     * Registers the split transform elements and immediately applies the latest zoom transform.
     */
    const registerZoomTransformElements = useCallback(
        (elements: ImageModalZoomTransformElements) => {
            transformElementsRef.current = elements;
            const { scale, translation } = zoomStateRef.current;
            applyZoomTransform(scale, translation);
        },
        [applyZoomTransform],
    );

    return {
        zoomState,
        zoomStateRef,
        commitZoomState,
        updateZoomState,
        registerZoomTransformElements,
    };
};
